import { memo } from 'react';
import { LuChartBar, LuInfo } from 'react-icons/lu';
import { FlightStatsCard, StatsGrid, StatItem, HighlightStat } from './FlightPlannerStyles';
import { LAT_TO_METERS, lngToMetersAtLat } from '../utils/coordinateUtils';
import { calculatePolygonArea } from '../utils/geometry';
import { BATTERY_FLIGHT_TIME_MIN } from '../constants/flightConstants';

/**
 * DSP Mission Estimate stats card.
 * Calculates and displays flight time, distance, DSP points, batteries, etc.
 * Only renders when waypoints exist.
 */
const DspMissionStats = ({ waypoints, polygonPoints, dspSettings }) => {
	if (waypoints.length === 0) return null;

	// Calculate total flight distance between waypoints
	const centerLat = waypoints[0].latitude;
	const lngToMeters = lngToMetersAtLat(centerLat);

	let totalDistance = 0;
	for (let i = 0; i < waypoints.length - 1; i++) {
		const dx = (waypoints[i + 1].longitude - waypoints[i].longitude) * lngToMeters;
		const dy = (waypoints[i + 1].latitude - waypoints[i].latitude) * LAT_TO_METERS;
		totalDistance += Math.sqrt(dx * dx + dy * dy);
	}

	// Calculate polygon area
	const flightAreaM2 = calculatePolygonArea(polygonPoints);

	// Total DSP plot area
	const totalDspArea = waypoints.length * dspSettings.plotArea;

	// Flight time calculation
	const travelTimeSeconds = totalDistance / dspSettings.speed;
	const dspTimeSeconds = dspSettings.stopAtDsp ? waypoints.length * dspSettings.stopDuration : 0;
	const totalTimeSeconds = travelTimeSeconds + dspTimeSeconds;
	const totalTimeMinutes = totalTimeSeconds / 60;

	// Battery estimate
	const estimatedBatteries = Math.ceil(totalTimeMinutes / BATTERY_FLIGHT_TIME_MIN);

	return (
		<FlightStatsCard>
			<h4>
				<LuChartBar size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{' '}
				Mission Estimate
			</h4>
			<HighlightStat>
				<div className="stat-label">Estimated Flight Time</div>
				<div className="stat-value">
					{totalTimeMinutes.toFixed(1)}
					<span className="stat-unit"> min</span>
				</div>
			</HighlightStat>
			<StatsGrid>
				<StatItem>
					<div className="stat-label">DSP Points</div>
					<div className="stat-value">{waypoints.length}</div>
				</StatItem>
				<StatItem>
					<div className="stat-label">Total Photos</div>
					<div className="stat-value">{waypoints.length}</div>
				</StatItem>
				<StatItem>
					<div className="stat-label">Flight Distance</div>
					<div className="stat-value">
						{(totalDistance / 1000).toFixed(2)}
						<span className="stat-unit">km</span>
					</div>
				</StatItem>
				{dspSettings.stopAtDsp && (
					<StatItem>
						<div className="stat-label">Stop per DSP</div>
						<div className="stat-value">
							{dspSettings.stopDuration}
							<span className="stat-unit">s</span>
						</div>
					</StatItem>
				)}
				<StatItem>
					<div className="stat-label">Total DSP Area</div>
					<div className="stat-value">
						{totalDspArea > 10000
							? `${(totalDspArea / 10000).toFixed(2)}`
							: totalDspArea.toFixed(0)}
						<span className="stat-unit">{totalDspArea > 10000 ? 'ha' : 'm²'}</span>
					</div>
				</StatItem>
				<StatItem>
					<div className="stat-label">Flight Area</div>
					<div className="stat-value">
						{flightAreaM2 > 10000
							? `${(flightAreaM2 / 10000).toFixed(2)}`
							: flightAreaM2.toFixed(0)}
						<span className="stat-unit">{flightAreaM2 > 10000 ? 'ha' : 'm²'}</span>
					</div>
				</StatItem>
				<StatItem>
					<div className="stat-label">Est. Batteries</div>
					<div className="stat-value">{estimatedBatteries}</div>
				</StatItem>
			</StatsGrid>
			<div
				style={{
					marginTop: '0.75rem',
					padding: '0.5rem',
					background: 'rgba(255, 193, 7, 0.1)',
					borderRadius: '4px',
					fontSize: '0.7rem',
					color: '#ffc107',
				}}>
				<LuInfo size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
				Based on {BATTERY_FLIGHT_TIME_MIN} min flight time per battery. Does not include
				takeoff, flight to resume point, or return to landing.
			</div>
		</FlightStatsCard>
	);
};

export default memo(DspMissionStats);
